import { ogImageMeta } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { TalkToJonButton } from "@/components/talk-to-jon";
import { RelatedGuides } from "@/components/related-guides";
import { SiteFooter } from "@/components/site-footer";

const TITLE = "Vibe Coding Help: Fix a Stuck AI-Built App | Vibing Company";
const DESC =
  "Your AI-built app broke, loops on the same error, or won't launch. Here's how to diagnose it yourself — and how to get a senior pair of eyes on it fast.";

const FAQS = [
  {
    q: "Why does my AI keep breaking the same thing?",
    a: "Usually the project has drifted: two competing patterns for the same job, dead files the model still reads, or a prompt history that contradicts itself. The fix is not another prompt — it's deleting the ambiguity so there's only one right answer left.",
  },
  {
    q: "My app works in preview but not when published. What now?",
    a: "Nine times out of ten it's environment: a key that only exists locally, a route that only renders client-side, or a build step that silently failed. Read the deployed build's logs first — the preview never runs the same code path.",
  },
  {
    q: "Is my vibe-coded app safe to launch?",
    a: "Not until someone has checked three things: row-level security on every table, no secret keys in the browser bundle, and validation on every write. These are the three that cause real incidents, and all three are quick to audit.",
  },
  {
    q: "Can someone just take over and finish it?",
    a: "Yes. That's most of what we do — we pick up half-built Lovable, Cursor and Claude Code projects, get them shipped, and hand them back in a state you can keep prompting on.",
  },
];

export const Route = createFileRoute("/guides/vibe-coding-help")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: "Vibe Coding Help: Fix a Stuck AI-Built App" },
      ...ogImageMeta("guides", "Share card: vibe coding help"),
      { property: "og:description", content: DESC },
      { property: "og:url", content: "https://vibing.inc/guides/vibe-coding-help" },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: "https://vibing.inc/guides/vibe-coding-help" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: "Vibe coding help: what to do when your AI-built app breaks",
          description: DESC,
          author: { "@type": "Organization", name: "the Vibing Company" },
          publisher: { "@type": "Organization", name: "the Vibing Company" },
          datePublished: "2026-09-21",
          mainEntityOfPage: "https://vibing.inc/guides/vibe-coding-help",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Guide,
});

const SECTIONS = [
  {
    h: "Start with the error, not the prompt",
    p: [
      "When an AI build breaks, the instinct is to prompt again. Resist it for ten minutes. Open the browser console, open the network tab, and read the deployed build's logs. The real message is almost always there in plain English, and it is almost never what the chat said went wrong.",
      "Write the exact error down before you touch anything. A model that can see the error text fixes it in one pass; a model guessing from your paraphrase will invent a second bug.",
    ],
  },
  {
    h: "The four failures that account for most stuck projects",
    p: [
      "Auth and permissions: the app works as you, fails as everyone else. That's row-level security or a missing policy, not the UI.",
      "Environment drift: works in preview, breaks live. A key, a build setting, or server-only code being imported into the browser.",
      "Pattern collision: two versions of the same feature both half-wired, so every fix breaks the other one.",
      "Silent data shape changes: the database returns a field the screen no longer expects, and the page renders blank instead of erroring.",
    ],
  },
  {
    h: "How to break the loop yourself",
    p: [
      "Shrink the problem. Delete the branch you don't need, comment out the half-finished feature, and get back to a version that runs. An AI cannot fix a project that never gets past the first error.",
      "Change one thing per prompt and check it. Batching five fixes into one request is how a loop starts.",
      "If three attempts in a row have not moved the error, the approach is wrong, not the wording. Step back and change the design of the thing rather than the phrasing of the ask.",
    ],
  },
  {
    h: "When to bring in a person",
    p: [
      "Bring someone in when money, personal data, or a launch date is involved. Those are the three cases where a wrong guess is expensive, and they're also the three that AI tools handle worst.",
      "We're full-stack designers — UI/UX people who learned the engineering — so you get the interface fixed and the stack underneath it fixed in the same pass, usually in days rather than weeks.",
    ],
  },
];

function Guide() {
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <main id="main" tabIndex={-1}>
        <section
          aria-labelledby="g-h"
          className="relative mx-auto max-w-[1100px] px-6 md:px-10 pt-[clamp(9rem,18vh,13rem)] pb-12"
        >
          <h1
            id="g-h"
            className="mt-8 font-display tracking-[-0.035em] leading-[1.02]"
            style={{ fontSize: "clamp(2rem, 4.4vw, 3.5rem)" }}
          >
            Vibe coding help.
          </h1>
          <p className="mt-10 max-w-[60ch] text-xl md:text-2xl leading-snug text-foreground">
            What to do when the AI keeps breaking the same thing, the published build doesn't match the preview, or the app is 80% finished and won't get further.
          </p>
        </section>

        <article className="mx-auto max-w-[1100px] px-6 md:px-10 py-12 md:py-20 space-y-16 md:space-y-24">
          {SECTIONS.map((s) => (
            <section
              key={s.h}
              className="grid md:grid-cols-12 gap-8 md:gap-10 border-t border-border/60 pt-12 md:pt-16"
            >
              <div className="md:col-span-4">
                <h2
                  className="mt-4 font-display tracking-[-0.03em] leading-[1.02]"
                  style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}
                >
                  {s.h}
                </h2>
              </div>
              <div className="md:col-span-8 space-y-5 text-base md:text-lg leading-relaxed text-foreground/90 max-w-[64ch]">
                {s.p.map((para, j) => (
                  <p key={j}>{para}</p>
                ))}
              </div>
            </section>
          ))}
        </article>

        <section
          aria-labelledby="faq-h"
          className="relative border-t border-border/60 mx-auto max-w-[1100px] px-6 md:px-10 py-20 md:py-32"
        >
          <h2 id="faq-h" className="fluid-h2 font-display max-w-[18ch]">
            Stuck-project questions, answered.
          </h2>
          <dl className="mt-14 border-t border-border">
            {FAQS.map((f) => (
              <div key={f.q} className="grid md:grid-cols-12 gap-6 py-8 border-b border-border">
                <dt className="md:col-span-5 font-display text-xl md:text-2xl tracking-[-0.02em] leading-snug">
                  {f.q}
                </dt>
                <dd className="md:col-span-6 md:col-start-7 text-base md:text-lg text-muted-foreground leading-relaxed">
                  {f.a}
                </dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="relative border-t border-border/60 mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-28 text-center">
          <h2 className="fluid-h2 font-display max-w-[24ch] mx-auto">
            Send the broken one. We'll tell you what's wrong.
          </h2>
          <p className="mt-6 text-lg text-muted-foreground max-w-[52ch] mx-auto">
            Thirty minutes, no pitch — we look at the project and say what it needs.
          </p>
          <div className="mt-10 flex justify-center">
            <TalkToJonButton variant="dark" immersive />
          </div>
          <div className="mt-8">
            <Link
              to="/services/vibe-coding-rescue"
              className="font-display text-electric underline underline-offset-4"
            >
              → Vibe coding rescue
            </Link>
          </div>
        </section>

        <RelatedGuides
          current="/guides/vibe-coding-help"
          service={{ to: "/services/vibe-coding-rescue", label: "Vibe coding rescue" }}
        />
      </main>
      <SiteFooter />
    </div>
  );
}
